import {
  ageSex,
  ageSexLong,
  CASE_STATE_TONE,
  fileLabel,
  fmtDayLongYear,
  fmtMonthDay,
  fmtMonthDayYear,
  fmtTime,
  fmtTimeZ,
  formatBytes,
  groupByDay,
  initials,
  initialsOf,
  zoneAbbr,
  zoneName,
} from '@concord/domain';
import type { CaseDetailView } from '@concord/domain/contracts';
import {
  Banner,
  CaseLayout,
  ClinicalFacts,
  Counters,
  Crumbs,
  DownloadLink,
  EntitlementChain,
  EventLog,
  FileList,
  FileRow,
  KeyValue,
  MetaStrip,
  NumberedQuestions,
  PageHead,
  Panel,
  PanelHead,
  People,
  PeopleGroup,
  PersonRow,
  Pill,
  Prose,
  ProseHeading,
  RevealValue,
} from '@concord/ui';
import { Fragment, type ReactNode } from 'react';
import { describeEvent } from './events';
import { TermsLink } from './TermsLink';

/**
 * The navigator's case screen (CON-05): state banner, entitlement chain, counters,
 * clinical card, records, event log, consultation, participants and offered times on
 * one page. `actions` and `banner` come from the caller so later milestones add behavior
 * without touching the layout.
 */
export function CaseScreen({
  c,
  tz,
  actions,
  banner,
  consultation,
  queueCrumb,
  invitationControls,
  guestControls,
  recordsAction,
  reveal,
}: {
  c: CaseDetailView;
  tz: string;
  actions?: ReactNode;
  banner?: ReactNode;
  consultation?: ReactNode;
  queueCrumb?: { label: string; to: string };
  /** Status and actions for each physician on the panel. */
  invitationControls?: (i: CaseDetailView['invitations'][number]) => ReactNode;
  guestControls?: (g: CaseDetailView['guests'][number]) => ReactNode;
  /** Beside the Records heading: the navigator's Add a record (DOC-06). */
  recordsAction?: ReactNode;
  /** Unmask one sensitive field (audited on the server). Without it, masked values show as is. */
  reveal?: (field: 'patientPhone' | 'dateOfBirth' | 'guestPhone', guestId?: string) => Promise<string | null>;
}) {
  const name = `${c.patient.firstName} ${c.patient.lastName}`;
  const submitted = c.submittedAt ? new Date(c.submittedAt) : null;
  const ageDays = submitted ? Math.floor((Date.now() - submitted.getTime()) / 86_400_000) : null;
  const typeSub = c.caseType?.specialties.slice(0, 2).join(' · ').toLowerCase();
  const staffEvents = c.events.filter((e) => e.visibility === 'staff').length;
  const size = c.attachments.reduce((a, x) => a + x.sizeBytes, 0);
  const whoSubmitted = c.submittedBy
    ? c.submittedBy.role === 'patient'
      ? `patient`
      : `${c.submittedBy.name} by phone`
    : 'patient';

  const whenLine = [
    c.caseType?.term,
    ageSex(c.demographics.age, c.demographics.gender),
    submitted
      ? `Submitted by ${whoSubmitted} ${fmtDayLongYear(submitted, tz)}, ${fmtTime(submitted, tz)} ${zoneAbbr(tz, submitted)}`
      : 'Not submitted',
    ageDays !== null
      ? `${ageDays === 0 ? 'today' : `${ageDays} ${ageDays === 1 ? 'day' : 'days'} old`}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const panel = c.invitations.filter((i) => i.state !== 'staged');
  const times = groupByDay(c.times, (t) => new Date(t.startsAt), tz);

  return (
    <>
      <Crumbs
        items={[
          { label: 'Cases', to: '/console/cases' },
          ...(queueCrumb ? [queueCrumb] : []),
          { label: c.reference ?? 'Draft', isRef: true },
        ]}
      />
      <PageHead
        title={
          <>
            <span className="ref" style={{ fontSize: '1rem' }}>
              {c.reference ?? 'Draft'}
            </span>{' '}
            {name}{' '}
            <Pill tone={CASE_STATE_TONE[c.state]} className="state">
              {c.stateLabel}
            </Pill>
            {c.qualityReview && <Pill tone="hold">Quality review</Pill>}
          </>
        }
        when={whenLine}
        actions={actions}
      />

      <EntitlementChain
        links={c.entitlement.links}
        planUntil={
          c.entitlement.planValidTo ? fmtMonthDayYear(new Date(c.entitlement.planValidTo), tz) : null
        }
        note={
          c.entitlement.valid
            ? c.entitlement.warnings.length
              ? `Chain valid · ${c.entitlement.warnings.join(', ')}`
              : 'Chain valid'
            : c.entitlement.message
        }
      />

      {banner ?? <DefaultBanner c={c} />}

      <Counters
        items={[
          { key: 'inv', label: 'Invited', value: c.counters.invited },
          {
            key: 'resp',
            label: 'Responded',
            value: c.counters.responded,
            small: `of ${c.counters.invited}`,
          },
          { key: 'open', label: 'Open', value: c.counters.open, small: 'awaiting reply', tone: 'wait' },
          { key: 'acc', label: 'Accepted', value: c.counters.accepted, tone: 'ok' },
          { key: 'dec', label: 'Declined', value: c.counters.declined, tone: 'bad' },
          { key: 'size', label: 'Panel size', value: c.panelSize, small: 'plan default' },
          {
            key: 'sel',
            label: 'Selection',
            value: c.selectionMode === 'manual' ? 'Manual' : 'Automated',
            compact: true,
          },
        ]}
      />

      <CaseLayout
        main={
          <>
            <Panel aria-label="Clinical card">
              <PanelHead
                title="Case"
                sub={c.channel === 'phone' ? 'As taken by phone' : 'As the patient submitted it'}
              />
              <ClinicalFacts
                items={[
                  {
                    key: 'type',
                    label: 'Case type',
                    value: c.caseType?.term ?? '—',
                    sub: typeSub ? typeSub.charAt(0).toUpperCase() + typeSub.slice(1) : undefined,
                  },
                  {
                    key: 'pt',
                    label: 'Patient',
                    value: c.demographics.volunteered
                      ? ageSexLong(c.demographics.age, c.demographics.gender)
                      : 'Not given',
                    sub: c.demographics.volunteered ? 'Volunteered at intake' : 'Declined at intake',
                  },
                  { key: 'lang', label: 'Source language', value: 'English', sub: 'Original text' },
                  {
                    key: 'terms',
                    label: 'Terms accepted',
                    value: c.termsVersion ? (
                      <TermsLink
                        kind="terms_of_use"
                        version={c.termsVersion}
                        who={`${c.patient.firstName} ${c.patient.lastName}`}
                        acceptedAt={c.termsAcceptedAt}
                        tz={tz}
                      />
                    ) : (
                      '—'
                    ),
                    sub: c.termsAcceptedAt
                      ? `${fmtMonthDay(new Date(c.termsAcceptedAt), tz)}, ${fmtTimeZ(new Date(c.termsAcceptedAt), tz)}`
                      : undefined,
                  },
                ]}
              />
              <Prose>
                <ProseHeading first>History</ProseHeading>
                {c.history
                  .split(/\n{2,}/)
                  .filter(Boolean)
                  .map((p) => (
                    <p key={p.slice(0, 40)}>{p}</p>
                  ))}
                <ProseHeading>Questions for the panel</ProseHeading>
                <NumberedQuestions questions={c.questions} />
              </Prose>
              <MetaStrip
                items={[
                  <Fragment key="by">
                    Submitted by <b>{c.submittedBy?.name ?? name}</b> (
                    {c.submittedBy?.role === 'navigator' ? 'navigator' : 'patient'})
                  </Fragment>,
                  <Fragment key="at">
                    {submitted ? (
                      <b>{`${fmtMonthDay(submitted, tz)}, ${submitted.getFullYear()}, ${fmtTimeZ(submitted, tz)}`}</b>
                    ) : (
                      'Not submitted'
                    )}
                  </Fragment>,
                  <Fragment key="started">
                    Intake started{' '}
                    <b>{`${fmtMonthDay(new Date(c.intakeStartedAt), tz)}, ${fmtTime(new Date(c.intakeStartedAt), tz)}`}</b>
                  </Fragment>,
                  <Fragment key="assist">
                    Writing assistant used{' '}
                    <b>
                      {c.assistantAccepted} {c.assistantAccepted === 1 ? 'suggestion' : 'suggestions'}{' '}
                      accepted
                    </b>
                  </Fragment>,
                ]}
              />
            </Panel>

            <Panel aria-label="Attachments">
              <PanelHead
                title="Records"
                sub={`${c.attachments.length} ${c.attachments.length === 1 ? 'file' : 'files'} · ${formatBytes(size)}`}
              >
                {recordsAction}
              </PanelHead>
              <FileList
                lockNote={
                  c.attachments.some((a) => a.locked)
                    ? 'Attached to the consultation on release. Files and descriptions can no longer change. Every download is logged.'
                    : 'Files lock when the case is released. Every download is logged.'
                }
              >
                {c.attachments.map((a) => {
                  const last = a.downloads?.at(-1);
                  return (
                    <FileRow
                      key={a.id}
                      ext={fileLabel(a.filename, a.contentType)}
                      name={a.description ?? a.filename}
                      meta={`${formatBytes(a.sizeBytes)} · uploaded by ${a.source === 'patient' ? 'patient' : (a.uploadedByName ?? 'navigator')} ${fmtMonthDay(new Date(a.createdAt), tz)}${a.externalSource ? ` · from ${a.externalSource}` : ''} · ${
                        last
                          ? `downloaded by ${last.by} ${fmtMonthDay(new Date(last.at), tz)}, ${fmtTime(new Date(last.at), tz)}`
                          : 'not yet downloaded'
                      }`}
                      action={<DownloadLink href={`/api/attachments/${a.id}/download`} />}
                    />
                  );
                })}
              </FileList>
            </Panel>

            <EventLog
              entries={[...c.events].reverse().map((e) => describeEvent(e, tz))}
              sub={`${c.events.length} ${c.events.length === 1 ? 'event' : 'events'} · ${staffEvents} internal`}
            />
          </>
        }
        side={
          <>
            {consultation ?? (
              <Panel aria-label="Consultation">
                <PanelHead title="Consultation" sub="Not scheduled" />
                <div className="pbody sub2">
                  A time is set when a physician accepts one of the times the patient offered.
                </div>
              </Panel>
            )}

            <Panel aria-label="Participants">
              <PanelHead title="Participants" sub={1 + panel.length + c.guests.length + (c.owner ? 1 : 0)} />
              <People>
                <PeopleGroup title="Patient" />
                <PersonRow
                  initials={initials(c.patient)}
                  name={name}
                  detail={
                    <>
                      {`${c.patient.email} · ${zoneName(c.patient.timezone)} · ${c.entitlement.customerName ?? ''}`}
                      {reveal && (c.patient.phone || c.demographics.dateOfBirth) && (
                        <span style={{ display: 'flex', gap: '.9rem', flexWrap: 'wrap', marginTop: '.2rem' }}>
                          {c.patient.phone && (
                            <span>
                              Phone{' '}
                              <RevealValue
                                masked={c.patient.phone}
                                label="the patient's phone"
                                reveal={() => reveal('patientPhone')}
                              />
                            </span>
                          )}
                          {c.demographics.dateOfBirth && (
                            <span>
                              Born{' '}
                              <RevealValue
                                masked={c.demographics.dateOfBirth}
                                label="the date of birth"
                                reveal={() => reveal('dateOfBirth')}
                              />
                            </span>
                          )}
                        </span>
                      )}
                    </>
                  }
                />
                {panel.length > 0 && (
                  <PeopleGroup
                    title="Panel"
                    right={`${c.counters.accepted} accepted · ${c.counters.declined} declined${
                      panel.some((i) => i.state === 'invited' && i.expiresAt)
                        ? ` · expires ${fmtTime(new Date(panel.find((i) => i.state === 'invited')!.expiresAt!), tz)}`
                        : ''
                    }`}
                  />
                )}
                {panel.map((i) => (
                  <PersonRow
                    key={i.id}
                    physician
                    initials={initialsOf(i.expertName)}
                    name={i.expertName}
                    tag={i.outOfPlan ? 'Out of plan' : undefined}
                    detail={`${i.specialty ?? ''} · ${i.poolName ?? ''} network · ${zoneName(i.timezone)}`}
                    status={invitationControls?.(i)}
                  />
                ))}
                {c.guests.length > 0 && <PeopleGroup title="Guests" right="Not panel members · not paid" />}
                {c.guests.map((g) => (
                  <PersonRow
                    key={g.id}
                    initials={initialsOf(g.name)}
                    name={g.name}
                    tag="Guest"
                    detail={
                      g.email || !g.phone || !reveal ? (
                        [g.note, g.email ?? g.phone].filter(Boolean).join(' · ')
                      ) : (
                        <>
                          {g.note ? `${g.note} · ` : ''}
                          <RevealValue
                            masked={g.phone}
                            label={`${g.name}'s phone`}
                            reveal={() => reveal('guestPhone', g.id)}
                          />
                        </>
                      )
                    }
                    status={guestControls?.(g)}
                  />
                ))}
                {c.owner && (
                  <>
                    <PeopleGroup title="Staff" />
                    <PersonRow
                      initials={initialsOf(c.owner.name)}
                      name={c.owner.name}
                      detail="Care navigator · case owner"
                    />
                  </>
                )}
              </People>
            </Panel>

            <Panel aria-label="Proposed times">
              <PanelHead
                title={`Times ${c.patient.firstName} offered`}
                sub={`${c.times.length} · ${zoneName(tz).replace(' Time', '')}`}
              />
              <div className="pbody" style={{ paddingTop: '.7rem' }}>
                {c.times.length ? (
                  <KeyValue
                    rows={times.map((g) => [
                      fmtDayShortFrom(g.items[0]!.startsAt, tz),
                      g.items.map((t) => fmtTime(new Date(t.startsAt), tz)).join(' · '),
                    ])}
                  />
                ) : (
                  <span className="sub2">
                    No times offered yet. At least one is needed to release the case.
                  </span>
                )}
                <p className="sub2" style={{ margin: '.8rem 0 0' }}>
                  Carried on every invitation. Physicians pick one; a physician who cannot make any of them
                  can request an alternative, which returns the case to you with their constraint.
                </p>
              </div>
            </Panel>
          </>
        }
      />
    </>
  );
}

function fmtDayShortFrom(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  }).format(new Date(iso));
}

/** The state banner when nothing more specific applies: what blocks release, and the fix (CON-08). */
function DefaultBanner({ c }: { c: CaseDetailView }) {
  if (c.state === 'draft') {
    return (
      <Banner
        tone="wait"
        title="Intake in progress"
        detail="The patient has not sent this case yet. Nothing goes to physicians until they do."
      />
    );
  }
  if (c.blockers.length) {
    return (
      <Banner
        tone="hold"
        title={c.state === 'on_hold' ? `On hold: ${c.holdReason ?? 'no reason given'}` : 'Not yet released'}
        detail={c.blockers.map((b) => b.message).join(' ')}
      />
    );
  }
  return null;
}
